import { useMemo, type ReactNode } from 'react';
import { ChevronDown, Gem } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import type { CustomizationSelection } from '../../domain/models/CustomizationSelection';
import type {
  GoldColorOption,
  GoldPurityOption,
  ParsedCustomizationOptions,
} from '../../domain/models/CustomizationOptions';

interface CustomizationDesktopOptionsProps {
  draft: CustomizationSelection;
  parsedOptions: ParsedCustomizationOptions;
  goldPurities: GoldPurityOption[];
  isApplying: boolean;
  onSelect: (key: string, value: string) => void;
}

function getMetalSwatchColor(color: string): string {
  if (color === 'Gold' || color === 'Yellow') return '#d4a853';
  if (color === 'Rose Gold' || color === 'Rose') return '#e8b4a0';
  if (color === 'White Gold' || color === 'White') return '#e8e8e8';
  return '#d4a853';
}

function DesktopRadioOption({
  label,
  selected,
  disabled,
  onSelect,
}: {
  label: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-2 text-sm uppercase text-[#1a2b6d]',
        disabled && 'cursor-not-allowed opacity-60'
      )}
    >
      <input
        type="radio"
        checked={selected}
        disabled={disabled}
        onChange={onSelect}
        className="h-4 w-4 accent-[var(--link-blue)]"
      />
      <span>{label}</span>
    </label>
  );
}

function GoldColorSelector({
  options,
  activeValue,
  disabled,
  onSelect,
}: {
  options: GoldColorOption[];
  activeValue: string | undefined;
  disabled?: boolean;
  onSelect: (value: string) => void;
}) {
  const activeOption =
    options.find((option) => option.color === activeValue) ?? options[0] ?? null;

  if (!activeOption) return null;

  if (options.length === 1) {
    return (
      <div className="flex items-center gap-2 rounded border border-[#e2e2e2] bg-white px-3 py-3">
        <span
          className="h-4 w-4 shrink-0 rounded-sm border border-[#d9d9d9]"
          style={{ backgroundColor: getMetalSwatchColor(activeOption.color) }}
          aria-hidden
        />
        <span className="text-sm font-semibold uppercase text-[#1a2b6d]">
          {activeOption.displayName}
        </span>
      </div>
    );
  }

  return (
    <div className="relative">
      <select
        value={activeOption.color}
        disabled={disabled}
        onChange={(event) => onSelect(event.target.value)}
        className="w-full appearance-none rounded border border-[#e2e2e2] bg-white py-2.5 pl-10 pr-9 text-sm font-semibold uppercase text-[#1a2b6d] outline-none"
      >
        {options.map((option) => (
          <option key={option.color} value={option.color}>
            {option.displayName}
          </option>
        ))}
      </select>
      <span
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 rounded-sm border border-[#d9d9d9]"
        style={{ backgroundColor: getMetalSwatchColor(activeOption.color) }}
        aria-hidden
      />
      <ChevronDown
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
        aria-hidden
      />
    </div>
  );
}

function DesktopOptionBlock({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded border border-[#e2e2e2] bg-white px-3 py-3', className)}>
      {children}
    </div>
  );
}

export function CustomizationDesktopOptions({
  draft,
  parsedOptions,
  goldPurities,
  isApplying,
  onSelect,
}: CustomizationDesktopOptionsProps) {
  const solitaireQualityActive = useMemo(
    () =>
      draft.solitaire_quality ??
      (draft.solitaire_clarity && draft.solitaire_color
        ? `${draft.solitaire_clarity}_${draft.solitaire_color}`
        : undefined),
    [draft.solitaire_clarity, draft.solitaire_color, draft.solitaire_quality]
  );

  return (
    <div className="space-y-2 bg-[#f0f0f0] px-0 py-3">
      {(parsedOptions.goldColors.length > 0 || goldPurities.length > 0) && (
        <div className="flex items-stretch gap-2">
          {parsedOptions.goldColors.length > 0 && (
            <GoldColorSelector
              options={parsedOptions.goldColors}
              activeValue={draft.metal_color}
              disabled={isApplying}
              onSelect={(value) => onSelect('metal_color', value)}
            />
          )}
          {goldPurities.length > 0 && (
            <DesktopOptionBlock className="flex items-center">
              <div className="flex flex-wrap gap-4">
                {goldPurities.map((option) => (
                  <DesktopRadioOption
                    key={option.metalPurity}
                    label={option.metalPurity}
                    selected={draft.metal_karatage === option.metalPurity}
                    disabled={isApplying}
                    onSelect={() => onSelect('metal_karatage', option.metalPurity)}
                  />
                ))}
              </div>
            </DesktopOptionBlock>
          )}
        </div>
      )}

      {!parsedOptions.isSolitaire && parsedOptions.diamonds.length > 0 && (
        <DesktopOptionBlock>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-sm font-semibold uppercase text-[#1a2b6d]">
              <Gem className="h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden />
              <span>Diamond :</span>
            </div>
            <div className="flex flex-wrap gap-4">
              {parsedOptions.diamonds.map((option) => (
                <DesktopRadioOption
                  key={option.value}
                  label={option.label}
                  selected={draft.diamond_type === option.value}
                  disabled={isApplying}
                  onSelect={() => onSelect('diamond_type', option.value)}
                />
              ))}
            </div>
          </div>
        </DesktopOptionBlock>
      )}

      {parsedOptions.isSolitaire && parsedOptions.solitaireCarats.length > 0 && (
        <DesktopOptionBlock>
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold uppercase text-[#1a2b6d]">Solitaire Carat</div>
            <div className="flex flex-wrap gap-4">
              {parsedOptions.solitaireCarats.map((option) => (
                <DesktopRadioOption
                  key={option.weight}
                  label={String(option.weight)}
                  selected={String(draft.solitaire_carat) === String(option.weight)}
                  disabled={isApplying}
                  onSelect={() => onSelect('solitaire_carat', String(option.weight))}
                />
              ))}
            </div>
          </div>
        </DesktopOptionBlock>
      )}

      {parsedOptions.isSolitaire && parsedOptions.solitaireClarities.length > 0 && (
        <DesktopOptionBlock>
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold uppercase text-[#1a2b6d]">Solitaire Clarity</div>
            <div className="flex flex-wrap gap-4">
              {parsedOptions.solitaireClarities.map((option) => (
                <DesktopRadioOption
                  key={option.name}
                  label={option.name}
                  selected={draft.solitaire_clarity === option.name}
                  disabled={isApplying}
                  onSelect={() => onSelect('solitaire_clarity', option.name)}
                />
              ))}
            </div>
          </div>
        </DesktopOptionBlock>
      )}

      {parsedOptions.isSolitaire && parsedOptions.solitaireColors.length > 0 && (
        <DesktopOptionBlock>
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold uppercase text-[#1a2b6d]">Solitaire Color</div>
            <div className="flex flex-wrap gap-4">
              {parsedOptions.solitaireColors.map((option) => (
                <DesktopRadioOption
                  key={option.name}
                  label={option.name}
                  selected={draft.solitaire_color === option.name}
                  disabled={isApplying}
                  onSelect={() => onSelect('solitaire_color', option.name)}
                />
              ))}
            </div>
          </div>
        </DesktopOptionBlock>
      )}

      {parsedOptions.isSolitaire && parsedOptions.solitaireQualities.length > 0 && (
        <DesktopOptionBlock>
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold uppercase text-[#1a2b6d]">Solitaire Quality</div>
            <div className="flex flex-wrap gap-4">
              {parsedOptions.solitaireQualities.map((option) => (
                <DesktopRadioOption
                  key={option.value}
                  label={option.label}
                  selected={solitaireQualityActive === option.value}
                  disabled={isApplying}
                  onSelect={() => onSelect('solitaire_quality', option.value)}
                />
              ))}
            </div>
          </div>
        </DesktopOptionBlock>
      )}
    </div>
  );
}
